import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "p-2 space-y-2" }
const _hoisted_2 = { class: "font-medium px-1 truncate" }
const _hoisted_3 = { class: "p-1" }
const _hoisted_4 = { class: "min-w-0 break-words" }
const _hoisted_5 = {
  key: 0,
  class: "px-2 py-3 text-center text-gray-500"
}
const _hoisted_6 = { class: "p-1 flex gap-1" }
const _hoisted_7 = {
  key: 0,
  class: "sr-only"
}

import { ListChecks, ListFilter, RefreshCw } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { i18n } from '@/plugins/internationalization/i18n';
import type { FacetOption } from './types';

interface Props {
    title: string;
    options: FacetOption[];
    modelValue?: (string | number)[];
    align?: 'start' | 'end';
    /** Offers Select all beside Clear; it ticks every option the search currently shows. */
    selectAll?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DataTableFacetFilter',
  props: {
    title: {},
    options: {},
    modelValue: { default: () => [] },
    align: { default: 'start' },
    selectAll: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const search = ref('');

const visibleOptions = computed(() => {
    const term = search.value.trim().toLowerCase();

    if (!term) {
        return props.options;
    }

    return props.options.filter((option) => option.label.toLowerCase().includes(term));
});

const selectedLabels = computed(() => props.options.filter((option) => props.modelValue.includes(option.value)).map((option) => option.label));

function isSelected(value: string | number): boolean {
    return props.modelValue.includes(value);
}

function toggle(value: string | number) {
    emit('update:modelValue', isSelected(value) ? props.modelValue.filter((v) => v !== value) : [...props.modelValue, value]);
}

const allVisibleSelected = computed(() => visibleOptions.value.every((option) => isSelected(option.value)));

function selectVisible() {
    emit('update:modelValue', [...props.modelValue, ...visibleOptions.value.map((option) => option.value).filter((value) => !isSelected(value))]);
}

function clear() {
    emit('update:modelValue', []);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), null, {
    default: _withCtx(() => [
      _createVNode(_unref(PopoverTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "sm",
            class: _normalizeClass(["px-2 gap-x-1.5 font-medium text-gray-500 data-[state=open]:bg-gray-100", { 'text-sky-600': _ctx.modelValue.length > 0 }]),
            "aria-label": _ctx.title,
            title: _unref(i18n).global.t('global.uiElements.dataTable.filter.texts.filterByColumn').replace('%COLUMN%', _ctx.title)
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ListFilter), {
                class: "size-4 flex-shrink-0",
                "aria-hidden": "true"
              }),
              (_ctx.modelValue.length > 0)
                ? (_openBlock(), _createBlock(_unref(Badge), {
                    key: 0,
                    variant: "secondary",
                    class: "rounded-md px-1.5 py-0 font-normal"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.modelValue.length), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["class", "aria-label", "title"])
        ]),
        _: 1
      }),
      _createVNode(_unref(PopoverContent), {
        align: _ctx.align,
        class: "p-0 w-max min-w-64 max-w-sm"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.title), 1),
            _createVNode(_unref(Input), {
              modelValue: search.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
              placeholder: _ctx.$t('global.uiElements.dataTable.filter.texts.search'),
              class: "h-9"
            }, null, 8, ["modelValue", "placeholder"])
          ]),
          _createVNode(_unref(Separator)),
          _createVNode(_unref(ScrollArea), { class: "max-h-64" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleOptions.value, (option) => {
                  return (_openBlock(), _createElementBlock("label", {
                    key: option.value,
                    class: "flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-100"
                  }, [
                    _createVNode(_unref(Checkbox), {
                      checked: isSelected(option.value),
                      "onUpdate:checked": ($event: any) => (toggle(option.value))
                    }, null, 8, ["checked", "onUpdate:checked"]),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(option.label), 1)
                  ]))
                }), 128)),
                (!visibleOptions.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t('global.uiElements.dataTable.filter.texts.noOptions')), 1))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }),
          _createVNode(_unref(Separator)),
          _createElementVNode("div", _hoisted_6, [
            (_ctx.selectAll)
              ? (_openBlock(), _createBlock(_unref(Button), {
                  key: 0,
                  variant: "ghost",
                  size: "sm",
                  class: "flex-1 justify-center",
                  disabled: allVisibleSelected.value,
                  onClick: selectVisible
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ListChecks), {
                      class: "size-4 mr-2",
                      "aria-hidden": "true"
                    }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('global.uiElements.multiSelect.selectAllButtonLabel')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
              : _createCommentVNode("", true),
            _createVNode(_unref(Button), {
              variant: "ghost",
              size: "sm",
              class: "flex-1 justify-center",
              disabled: _ctx.modelValue.length === 0,
              onClick: clear
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(RefreshCw), {
                  class: "size-4 mr-2",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" " + _toDisplayString(_ctx.$t('global.uiElements.dataTable.filter.buttons.clear')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          (selectedLabels.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(selectedLabels.value.join(', ')), 1))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["align"])
    ]),
    _: 1
  }))
}
}

})